import { useEffect, useRef, useState } from "react";
import apiInstance from "../../apiInstance";
import PopupModal from "../Popups/PopupModal";
import { useNavigate } from "react-router-dom";

const DevadminSupportTickets = () => {
    const [tickets, setTickets] = useState([]);
    const [totalTickets, setTotalTickets] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(50);
    const [selectedItems, setSelectedItems] = useState([]);
    const [bulkAction, setBulkAction] = useState("");
    const [reload, setReload] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filters, setFilters] = useState({
        status: "",
        category: "",
        priority: ""
    });
    const [error, setError] = useState("");
    const popupRef = useRef();
    const navigate = useNavigate();

    const addTicketHandler = (ticket) => {
        setTickets((prev) => [ticket, ...prev]);
    };

    const deleteTicketHandler = (ticketId) => {
        setTickets((prev) => prev.filter((item) => item.id !== ticketId));
    };

    const updateTicketHandler = (ticket) => {
        const updatedTickets = tickets.map((item) =>
            item.id === ticket.id ? { ...item, ...ticket } : item
        );
        setTickets(updatedTickets);
    };

    const closeTicket = (e, ticketId) => {
        
        e.stopPropagation()
        apiInstance.put(`/devadmin/support-ticket/${ticketId}`, {status: "closed"})
            .then((response) => {
                const returnedTicket = response.data.ticket;
                updateTicketHandler(returnedTicket);
            })
            .catch((error) => {
                console.error("Unexpected error:", error);
                if (error.response) {
                    setError(error.response.data.message || 'Something went wrong, please try again later.');
                } 
            });
    }

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = tickets.map((item) => item._id);
            setSelectedItems(allIds);
        } else {
            setSelectedItems([]);
        }
    };

    const handleSelect = (selectedId) => {
        setSelectedItems((prevSelected) => {
            if (prevSelected.includes(selectedId)) {
                return prevSelected.filter((id) => selectedId !== id);
            } else {
                return [...prevSelected, selectedId];
            }
        });
    };

    const handleBulkAction = async () => {
        switch (bulkAction) {
            case "delete":
                for (const id of selectedItems) {
                    try {
                        const res = await apiInstance.delete(
                            `/devadmin/support-ticket/${id}`
                        );

                        setTickets((prev) =>
                            prev.filter((item) => item.id !== id)
                        );
                    } catch (error) {
                        console.error(`Error deleting ticket:`, error);
                        setError(`Error deleting ticket ${id}.`);
                    }
                }
                setReload((prev) => prev + 1);
                break;
        }
    };

    const handleCurrentPageChange = (p) => {
        const totalPages = Math.ceil(totalTickets / perPage);
        const value = Math.min(totalPages, Math.max(1, p));
        setCurrentPage(value);
        setSelectedItems([]);
    };

    const renderPaginationControls = () => {
        let totalPages = 0;
        let startItem = 0;
        let endItem = 0;
        if (totalTickets > 0) {
            totalPages = Math.ceil(totalTickets / perPage);
            startItem = (currentPage - 1) * perPage + 1;
            endItem = Math.min(currentPage * perPage, totalTickets);
        }

        return (
            <div className="mjsdev-webs-pagination-wrapper">
                Per Page
                <select
                    value={perPage}
                    onChange={(e) => {
                        setPerPage(Number(e.target.value));
                        handleCurrentPageChange(1);
                    }}
                >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                </select>
                <span>
                    Showing {startItem}-{endItem} of {totalTickets}
                </span>
                <button
                    onClick={() => handleCurrentPageChange(1)}
                    disabled={currentPage === 1}
                >
                    &laquo;
                </button>
                <button
                    onClick={() => handleCurrentPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    &lsaquo;
                </button>
                <input
                    type="number"
                    value={currentPage}
                    onChange={(e) =>
                        handleCurrentPageChange(Number(e.target.value))
                    }
                />
                <span>of {totalPages}</span>
                <button
                    onClick={() => handleCurrentPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    &rsaquo;
                </button>
                <button
                    onClick={() => handleCurrentPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                >
                    &raquo;
                </button>
            </div>
        );
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(search || "");
        }, 400);

        return () => clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        const fetchTickets = async () => {
            apiInstance
                .get(`/devadmin/support-ticket`, {
                    params: {
                        page: currentPage,
                        limit: perPage,
                        search: debouncedSearch,
                        ...filters,
                    },
                })
                .then((response) => {
                    const fetchedTickets = response.data.tickets || [];
                    setTickets(fetchedTickets);
                    setTotalTickets(response.data.pagination.total || 0);
                    setSelectedItems([]);
                })
                .catch((error) => {
                    console.error("Error fetching data:", error);
                    setError("Failed to load tickets.");
                });
        };
        fetchTickets();
    }, [currentPage, perPage, reload, filters, debouncedSearch]);

    return (
        <>
            <div className="mjsdev-main">
                <div className="mjsdev-webs-header">
                    <h1></h1>
                    <div>
                        <button
                            className="mjs-btn"
                            onClick={(e) => {
                                if (popupRef.current) {
                                    popupRef.current.openPopup("addTicket");
                                }
                            }}
                        >
                            Add New Ticket
                        </button>
                    </div>
                </div>

                <div className="mjsdev-webs-toolbar">
                    <div className="mjsdev-webs-bulk_actions">
                        <select
                            value={bulkAction}
                            onChange={(e) => setBulkAction(e.target.value)}
                        >
                            <option value="">Select</option>
                            <option value="delete">Delete</option>
                        </select>
                        <button
                            className="mjs-btn"
                            onClick={handleBulkAction}
                            disabled={!bulkAction || selectedItems.length === 0}
                        >
                            Apply
                        </button>
                    </div>

                    <div className="mjsdev-webs-filters">
                        <div className="mjsdev-search-wrapper">
                            <input
                                style={{width: "200px"}}
                                type="text"
                                placeholder="Search..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            {search && (
                                <i
                                    className="fa-solid fa-times"
                                    onClick={() => setSearch("")}
                                    title="Clear search"
                                ></i>
                            )}
                        </div>
                        <select
                            value={filters.category}
                            onChange={(e) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    category: e.target.value,
                                }));
                                setCurrentPage(1);
                            }}
                        >
                            <option value="">Category</option>
                            <option value="billing">Billing</option>
                            <option value="technical">Technical</option>
                            <option value="account">Account</option>
                            <option value="website">Website</option>
                            <option value="feature_request">Feature Request</option>
                            <option value="new_website_request">Website Request</option>
                            <option value="other">Other</option>
                        </select>
                        <select
                            value={filters.priority}
                            onChange={(e) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    priority: e.target.value,
                                }));
                                setCurrentPage(1);
                            }}
                        >
                            <option value="">Priority</option>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                        </select>
                        <select
                            value={filters.status}
                            onChange={(e) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    status: e.target.value,
                                }));
                                setCurrentPage(1);
                            }}
                        >
                            <option value="">Status</option>
                            <option value="open">Open</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                        </select>
                    </div>

                    <div className="mjsdev-webs-pgn-top">
                        {renderPaginationControls()}
                    </div>
                </div>

                {error && <p className="mjs-error">{error}</p>}

                <table className="mjsdev-table hover-enabled">
                    <thead>
                        <tr>
                            <th>
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={
                                        tickets.length > 0 &&
                                        selectedItems.length === tickets.length
                                    }
                                />
                            </th>
                            <th>Ticket ID</th>
                            <th>Customer</th>
                            <th>Subject</th>
                            <th>Category</th>
                            <th>Priority</th>
                            <th>Status</th>                            
                            <th>Date</th>
                            <th></th>
                        </tr>
                    </thead>

                    {tickets.length > 0 ? (
                        <tbody>
                            {tickets.map((ticket) => (
                                <tr 
                                    key={ticket.id}
                                    onClick={() => navigate(`/devadmin/support-tickets/${ticket._id}`)}
                                >
                                    <td>
                                        <input
                                            type="checkbox"
                                            onChange={() =>
                                                handleSelect(ticket.id)
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            checked={selectedItems.includes(
                                                ticket.id
                                            )}
                                        />
                                    </td>
                                    <td>#{ticket.ticket_number || ''}</td>
                                    <td>{ticket.user?.name || "-"}</td>
                                    <td>{ticket.subject || "-"}</td>
                                    <td>{ticket.category || "-"}</td>
                                    <td>{ticket.priority || "--"}</td>
                                    <td>{ticket.status || "-"}</td>                                    
                                    <td>
                                        {ticket.createdAt
                                            ? new Date(
                                                  ticket.createdAt
                                              ).toDateString()
                                            : "—"}
                                    </td>
                                    <td className="mjsdev-table-actions">
                                        {ticket.status !== 'closed' ? (
                                            <i
                                                onClick={(e) => closeTicket(e, ticket._id)}
                                                title="Close Ticket"
                                                className="fa-solid fa-circle"
                                                style={{color: 'red'}}
                                            ></i>
                                        ) : (
                                            <i                                
                                                title="Ticket closed"
                                                className="fa-solid fa-circle-check"
                                                style={{color: 'green'}}
                                            ></i>
                                        )}
                                        <i
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (popupRef.current) {
                                                    popupRef.current.openPopup(
                                                        "editTicket",
                                                        { ticket }
                                                    );
                                                }
                                            }}
                                            title="Edit Ticket"
                                            className="fa-solid fa-pen-to-square"
                                        ></i>
                                        <i
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (popupRef.current) {
                                                    popupRef.current.openPopup(
                                                        "deleteTicket",
                                                        { ticketId: ticket._id }
                                                    );
                                                }
                                            }}
                                            title="Delete Ticket"
                                            className="fa-solid fa-trash"
                                        ></i>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    ) : (
                        <tbody>
                            <tr>
                                <td colSpan="8" className="mjsdev-msg">
                                    No tickets found.
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>

                <div className="mjsdev-webs-pgn-bottom">
                    {renderPaginationControls()}
                </div>
            </div>
            <PopupModal
                ref={popupRef}
                addTicket={addTicketHandler}
                deleteTicket={deleteTicketHandler}
                updateTicket={updateTicketHandler}
            />
        </>
    );
};

export default DevadminSupportTickets;
